// Growth journey (gentle gamification).
import { useState, useEffect } from "react";
import { Star, ChevronRight, Check, Crown, Heart, Award } from "lucide-react";
import { useLang } from "../../i18n.jsx";
import { api } from "../api.js";
import { FluentEmoji, Stars } from "../emoji.jsx";
import { fmt, toFa } from "../format.js";
import { C, FONT } from "../../design-system.js";

// ── Growth Journey (Gentle gamification) ──────────────────────────────────────
export function JourneyScreen({ child, onBack }) {
  const { t, dir } = useLang();
  const [stories, setStories] = useState([]);

  useEffect(() => {
    if (child) {
      api(`/stories/child/${child.id}`).then(setStories).catch(() => setStories([]));
    }
  }, [child]);

  // The monthly journey is measured against the PLAN (2 stories/week ≈ 8/month),
  // not how many have been delivered so far — otherwise "1 of 1" looks complete
  // while more stories are still coming this month.
  const STORIES_PER_MONTH = 8;
  const heardCount   = stories.filter(s => s.listened).length;
  const storiesTotal = STORIES_PER_MONTH;
  const storiesHeard = Math.min(heardCount, storiesTotal);
  const half = Math.ceil(storiesTotal / 2);

  const milestones = [
    { icon: Heart,  color: C.red,    title: t("اولین داستان"),  sub: t("{name} اولین قصه را شنید", { name: child.name }),   done: storiesHeard >= 1 },
    { icon: Award,  color: C.gold,   title: t("۳ قصه شنیده"),   sub: t("سه قصه با موفقیت شنیده شد"),    done: storiesHeard >= 3 },
    { icon: Crown,  color: C.purple, title: t("نیمه‌راه ماه"),    sub: t("{a} از {b} داستان", { a: toFa(half), b: toFa(storiesTotal) }),            done: storiesHeard >= half, current: storiesHeard > 0 && storiesHeard < half },
    { icon: Star,   color: C.teal,   title: t("قهرمان ماه"),     sub: t("تمام {n} داستان کامل شود", { n: toFa(storiesTotal) }),   done: storiesHeard >= storiesTotal },
  ];

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: FONT, position: "relative" }}>
      <div style={{ position: "fixed", inset: 0, background: `linear-gradient(180deg, ${C.bgDeep} 0%, ${C.bg} 40%)`, zIndex: 0 }}><Stars count={30} /></div>

      <div style={{ position: "relative", zIndex: 1, padding: "20px 20px 0" }}>
        <button onClick={onBack} className="press" style={{ background: "none", border: "none", color: C.gold, fontSize: 16, cursor: "pointer", fontFamily: FONT, padding: 0, marginBottom: 20, display: "flex", alignItems: "center", gap: 4 }}>
          <ChevronRight size={18} style={{ transform: dir === "ltr" ? "rotate(180deg)" : "none" }} /> {t("بازگشت")}
        </button>
      </div>

      <div style={{ position: "relative", zIndex: 1, padding: "0 16px 100px", animation: "slideUp 0.35s ease" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <FluentEmoji anim="float" size={56} style={{ marginBottom: 8 }}>{child.avatar}</FluentEmoji>
          <h2 style={{ color: C.cream, fontSize: 22, fontWeight: 800, margin: "0 0 4px" }}>{t("سفر رشد {name}", { name: child.name })}</h2>
          <p style={{ color: C.textMid, fontSize: 14, lineHeight: 1.7 }}>{t("هر داستان، یک قدم به سوی شجاعت بیشتر 🌙")}</p>
        </div>

        {/* Progress summary card */}
        <div style={{ background: `linear-gradient(135deg, ${C.gold}14, ${C.gold}06)`, border: `1px solid ${C.borderGold}`, borderRadius: 18, padding: 20, marginBottom: 24, textAlign: "center" }}>
          <div style={{ color: C.gold, fontSize: 30, fontWeight: 800 }}>{t("{a} از {b}", { a: fmt(storiesHeard), b: fmt(storiesTotal) })}</div>
          <div style={{ color: C.cream, fontSize: 15, marginTop: 2, marginBottom: 14 }}>{t("داستان این ماه شنیده شده")}</div>
          <div style={{ height: 8, background: C.border, borderRadius: 8, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${storiesTotal > 0 ? (storiesHeard/storiesTotal)*100 : 0}%`, background: `linear-gradient(90deg, ${C.gold}, ${C.goldLight})`, borderRadius: 8, transition: "width 0.8s ease" }} />
          </div>
        </div>

        {/* Milestone timeline */}
        <div style={{ position: "relative", paddingRight: 8 }}>
          {milestones.map((m, i) => {
            const Icon = m.icon;
            return (
              <div key={i} style={{ display: "flex", gap: 16, marginBottom: 8, position: "relative" }}>
                {i < milestones.length - 1 && (
                  <div style={{ position: "absolute", right: 23, top: 48, bottom: -8, width: 2, background: m.done ? C.gold + "44" : C.border }} />
                )}
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: m.done ? `${m.color}22` : (m.current ? `${m.color}15` : C.bgCard), border: `2px solid ${m.done || m.current ? m.color : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1, animation: m.current ? "pulse2 2s ease-in-out infinite" : "none" }}>
                  <Icon size={20} color={m.done || m.current ? m.color : C.textDim} fill={m.done ? m.color : "none"} />
                </div>
                <div style={{ flex: 1, background: C.bgCard, border: `1px solid ${m.current ? m.color + "44" : C.border}`, borderRadius: 14, padding: "13px 16px", marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ color: m.done || m.current ? C.cream : C.textMid, fontSize: 15, fontWeight: 700 }}>{m.title}</span>
                    {m.done && <Check size={16} color={C.teal} />}
                    {m.current && <span style={{ color: m.color, fontSize: 12, fontWeight: 700 }}>{t("اکنون")}</span>}
                  </div>
                  <div style={{ color: C.textMid, fontSize: 13, marginTop: 3 }}>{m.sub}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Warm bonding note */}
        <div style={{ background: `${C.teal}0D`, border: `1px solid ${C.tealDim}`, borderRadius: 16, padding: 18, marginTop: 16, textAlign: "center" }}>
          <Heart size={22} color={C.teal} style={{ marginBottom: 8 }} />
          <p style={{ color: C.cream, fontSize: 14, lineHeight: 1.8, margin: 0 }}>
            {t("شنیدن داستان‌ها «در کنار هم»، لحظه‌های گرمی می‌سازد که {name} هرگز فراموش نمی‌کند.", { name: child.name })}
          </p>
        </div>
      </div>
    </div>
  );
}

